import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Plus, Wrench } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { Aviso, Cargando, FilaHecha, INPUT_CLASS, LABEL_CLASS } from "./ui";
import type { PasoProps } from "./types";

/**
 * PASO 3 (citas): los profesionales. ES EL PASO QUE FALTABA SIEMPRE y el motivo
 * de todo este asistente: el cliente crea sus servicios y se queda ahí, así que
 * el motor de huecos (`public-appointments/availability.ts`) no encuentra a
 * nadie que pueda atender y el widget responde "no hay disponibilidad".
 *
 * Un profesional solo cuenta cuando tiene LAS DOS COSAS:
 *   1. horario semanal propio (`SpecialistSchedule`), y
 *   2. al menos un servicio asignado.
 * Por eso aquí se crean las tres cosas de una vez, y a los que ya existen a
 * medias se les ofrece arreglarlo con un botón.
 *
 * El horario se copia del CENTRO por defecto: es lo que pasa en el 90 % de los
 * casos (la peluquera trabaja las horas que abre la peluquería) y se puede
 * afinar después en su agenda, que es donde están las vacaciones y los días
 * sueltos.
 */

interface Servicio {
  id: string;
  name: string;
}

interface Profesional {
  id: string;
  firstName: string;
  lastName: string | null;
  services?: { service: { id: string; name: string } }[];
}

interface TurnoDia {
  start: string;
  end: string;
}

const DIAS = [
  { dow: 1, corto: "L", largo: "Lunes" },
  { dow: 2, corto: "M", largo: "Martes" },
  { dow: 3, corto: "X", largo: "Miércoles" },
  { dow: 4, corto: "J", largo: "Jueves" },
  { dow: 5, corto: "V", largo: "Viernes" },
  { dow: 6, corto: "S", largo: "Sábado" },
  { dow: 0, corto: "D", largo: "Domingo" },
] as const;

/** Turnos del centro por día de la semana, ya en el formato del profesional. */
type TurnosCentro = Record<number, TurnoDia[]>;

/**
 * Convierte el horario del centro (`{"1": {isOpen, shifts:[{startTime,endTime}]}}`)
 * al formato del horario de un profesional (`{start, end}`). Son dos formatos
 * distintos en la base de datos, y confundirlos deja al profesional sin horas.
 */
function turnosDelCentro(weeklySchedule: unknown): TurnosCentro {
  const salida: TurnosCentro = {};
  if (!weeklySchedule || typeof weeklySchedule !== "object") return salida;
  for (const [clave, valor] of Object.entries(
    weeklySchedule as Record<string, unknown>,
  )) {
    const dow = Number(clave);
    if (!Number.isInteger(dow) || dow < 0 || dow > 6) continue;
    if (!valor || typeof valor !== "object") continue;
    const { isOpen, shifts } = valor as {
      isOpen?: boolean;
      shifts?: { startTime?: string; endTime?: string }[];
    };
    if (isOpen !== true || !Array.isArray(shifts)) continue;
    salida[dow] = shifts
      .filter((t) => t.startTime && t.endTime)
      .map((t) => ({ start: t.startTime as string, end: t.endTime as string }));
  }
  return salida;
}

/** Los 7 días en el formato que exige `PUT /specialists/:id/schedule`. */
function construirDias(
  diasElegidos: readonly number[],
  turnos: TurnosCentro,
): { dayOfWeek: number; isWorking: boolean; shifts: TurnoDia[] }[] {
  return [0, 1, 2, 3, 4, 5, 6].map((dow) => {
    const trabaja = diasElegidos.includes(dow);
    const suyos = turnos[dow] ?? [];
    return {
      dayOfWeek: dow,
      // Si el centro no tiene horas ese día no se le puede dar turno: se marca
      // como no laborable en vez de dejarlo "trabajando" sin horas (que es
      // justo el estado que no genera ni un hueco).
      isWorking: trabaja && suyos.length > 0,
      shifts: trabaja ? suyos.map((t) => ({ ...t })) : [],
    };
  });
}

export default function PasoProfesionales({
  estado,
  refrescar,
  registrarAvance,
  registrarTextoAvance,
}: PasoProps) {
  const { showToast } = useGlobalToast();
  const [servicios, setServicios] = useState<Servicio[] | null>(null);
  const [profesionales, setProfesionales] = useState<Profesional[]>([]);
  const [turnosCentro, setTurnosCentro] = useState<TurnosCentro>({});
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [serviciosElegidos, setServiciosElegidos] = useState<string[]>([]);
  const [diasElegidos, setDiasElegidos] = useState<number[]>([]);
  const [creando, setCreando] = useState(false);
  const [arreglando, setArreglando] = useState<string | null>(null);
  /** Este asistente ha creado uno completo: no hace falta esperar al refresco. */
  const [creadoListo, setCreadoListo] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [svc, esp, ajustes] = await Promise.all([
        apiClient.get("/services", { params: { includeInactive: "false" } }),
        apiClient.get("/specialists", { params: { includeInactive: "false" } }),
        apiClient.get("/appointment-settings"),
      ]);
      const listaServicios: Servicio[] = svc.data?.data ?? [];
      setServicios(listaServicios);
      setProfesionales(esp.data?.data ?? []);
      const turnos = turnosDelCentro(ajustes.data?.data?.weeklySchedule);
      setTurnosCentro(turnos);
      // Preseleccionado: todos sus servicios y los días que abre el centro. Es
      // lo más probable, y así el formulario se puede enviar sin tocar nada.
      setServiciosElegidos(listaServicios.map((s) => s.id));
      setDiasElegidos(
        DIAS.map((d) => d.dow).filter((dow) => (turnos[dow] ?? []).length > 0),
      );
    } catch {
      setServicios([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const alternar = (lista: number[], valor: number) =>
    lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];

  const crear = async () => {
    const pila = nombre.trim();
    if (!pila) {
      showToast("Escribe el nombre del profesional", "error");
      return;
    }
    if (!estado.sede) {
      showToast("No se ha podido identificar tu sede", "error");
      return;
    }
    if (serviciosElegidos.length === 0) {
      showToast("Marca qué servicios hace esta persona", "error");
      return;
    }
    const dias = construirDias(diasElegidos, turnosCentro);
    if (!dias.some((d) => d.isWorking)) {
      showToast(
        "Marca al menos un día en el que trabaje (y que el centro esté abierto)",
        "error",
      );
      return;
    }

    try {
      setCreando(true);
      const res = await apiClient.post("/specialists", {
        locationId: estado.sede.id,
        firstName: pila,
        lastName: apellido.trim() || null,
        serviceIds: serviciosElegidos,
        isActive: true,
      });
      const id: string | undefined = res.data?.data?.id;
      if (id) {
        // El horario va en una llamada aparte (endpoint de siempre). Si fallara,
        // el profesional ya existe: se avisa y se puede arreglar con el botón de
        // abajo, no se pierde el trabajo.
        try {
          await apiClient.put(`/specialists/${id}/schedule`, { days: dias });
          setCreadoListo(true);
        } catch (err) {
          showToast(
            extractApiErrorMessage(
              err,
              "Se creó la persona, pero no su horario. Pulsa «Ponerle horario» para terminarlo.",
            ),
            "error",
          );
        }
      }
      setNombre("");
      setApellido("");
      await cargar();
      refrescar();
      showToast(`${pila} ya puede recibir citas`);
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo crear el profesional"),
        "error",
      );
    } finally {
      setCreando(false);
    }
  };

  /** Arregla a quien ya existe pero está a medias: horario y/o servicios. */
  const arreglar = async (
    id: string,
    faltaHorario: boolean,
    faltanServicios: boolean,
  ) => {
    try {
      setArreglando(id);
      if (faltanServicios) {
        const todos = (servicios ?? []).map((s) => s.id);
        if (todos.length === 0) {
          showToast("Primero crea algún servicio", "error");
          return;
        }
        await apiClient.put(`/specialists/${id}`, { serviceIds: todos });
      }
      if (faltaHorario) {
        const dias = construirDias(
          DIAS.map((d) => d.dow).filter(
            (dow) => (turnosCentro[dow] ?? []).length > 0,
          ),
          turnosCentro,
        );
        if (!dias.some((d) => d.isWorking)) {
          showToast(
            "Tu centro no tiene horas abiertas: repasa el paso del horario",
            "error",
          );
          return;
        }
        await apiClient.put(`/specialists/${id}/schedule`, { days: dias });
      }
      await cargar();
      refrescar();
      setCreadoListo(true);
      showToast("Listo, ya puede recibir citas");
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo completar"),
        "error",
      );
    } finally {
      setArreglando(null);
    }
  };

  const incompletos = estado.citas?.profesionalesIncompletos ?? [];
  const idsIncompletos = new Set(incompletos.map((p) => p.id));
  const listos = profesionales.filter((p) => !idsIncompletos.has(p.id));

  const avanzar = useCallback(async (): Promise<boolean> => {
    if (creadoListo || (estado.citas?.profesionalesListos ?? 0) > 0) return true;
    showToast(
      "Necesitas a alguien que atienda: sin un profesional con horario, el widget no ofrece ni una hora",
      "error",
    );
    return false;
  }, [creadoListo, estado.citas?.profesionalesListos, showToast]);

  useEffect(() => {
    registrarAvance(avanzar);
    registrarTextoAvance(null);
    return () => registrarAvance(null);
  }, [avanzar, registrarAvance, registrarTextoAvance]);

  if (!servicios) return <Cargando texto="Cargando tu equipo…" />;

  return (
    <div className="space-y-4">
      <Aviso tono="ojo">
        Esto es lo que más se olvida. Aunque tengas servicios y horario,{" "}
        <strong>si no hay nadie que atienda, el widget dirá que no hay horas
        libres</strong>. Cada persona necesita sus días de trabajo y qué
        servicios hace.
      </Aviso>

      {listos.length > 0 && (
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 px-3">
          {listos.map((p) => (
            <FilaHecha
              key={p.id}
              titulo={`${p.firstName} ${p.lastName ?? ""}`.trim()}
              detalle={
                p.services && p.services.length > 0
                  ? p.services.map((s) => s.service.name).join(", ")
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {incompletos.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 divide-y divide-amber-100">
          {incompletos.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 p-3 flex-wrap sm:flex-nowrap"
            >
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-amber-900 truncate">
                  {p.nombre || "Sin nombre"}
                </p>
                <p className="text-xs text-amber-800">
                  {p.faltaHorario && p.faltanServicios
                    ? "No tiene horario ni servicios: no puede recibir citas"
                    : p.faltaHorario
                      ? "No tiene horario: no puede recibir citas"
                      : "No tiene servicios asignados: no puede recibir citas"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => arreglar(p.id, p.faltaHorario, p.faltanServicios)}
                disabled={arreglando === p.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                <Wrench className="w-3.5 h-3.5" />
                {arreglando === p.id ? "Arreglando…" : "Arreglarlo por mí"}
              </button>
            </div>
          ))}
          <p className="px-3 py-2 text-[11px] text-amber-800">
            «Arreglarlo por mí» le pone el horario de tu centro y todos tus
            servicios. Luego puedes afinarlo en su agenda.
          </p>
        </div>
      )}

      <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
        <p className="text-sm font-medium text-gray-900">
          {profesionales.length === 0
            ? "Añade a la primera persona que atiende"
            : "Añadir a otra persona"}
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="wizard-prof-nombre">
              Nombre
            </label>
            <input
              id="wizard-prof-nombre"
              className={INPUT_CLASS}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="María"
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="wizard-prof-apellido">
              Apellido (opcional)
            </label>
            <input
              id="wizard-prof-apellido"
              className={INPUT_CLASS}
              value={apellido}
              onChange={(e) => setApellido(e.target.value)}
              placeholder="López"
            />
          </div>
        </div>

        <div>
          <p className={LABEL_CLASS}>¿Qué días trabaja?</p>
          <div className="flex flex-wrap gap-1.5">
            {DIAS.map((d) => {
              const abiertoElCentro = (turnosCentro[d.dow] ?? []).length > 0;
              const activo = diasElegidos.includes(d.dow);
              return (
                <button
                  key={d.dow}
                  type="button"
                  disabled={!abiertoElCentro}
                  title={
                    abiertoElCentro
                      ? d.largo
                      : `${d.largo}: tu centro está cerrado`
                  }
                  onClick={() => setDiasElegidos((prev) => alternar(prev, d.dow))}
                  className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${
                    !abiertoElCentro
                      ? "bg-gray-50 text-gray-300 cursor-not-allowed"
                      : activo
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {d.corto}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Trabajará en el horario de tu centro. Los días en gris están
            cerrados.
          </p>
        </div>

        <div>
          <p className={LABEL_CLASS}>¿Qué servicios hace?</p>
          {servicios.length === 0 ? (
            <p className="text-xs text-amber-700">
              Todavía no tienes servicios: vuelve al paso anterior y crea alguno.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {servicios.map((s) => {
                const activo = serviciosElegidos.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() =>
                      setServiciosElegidos((prev) =>
                        prev.includes(s.id)
                          ? prev.filter((v) => v !== s.id)
                          : [...prev, s.id],
                      )
                    }
                    className={`px-2.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      activo
                        ? "bg-primary-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={crear}
          disabled={creando || servicios.length === 0}
          className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {creando ? "Creando…" : "Añadir profesional"}
        </button>
      </div>
    </div>
  );
}
